import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Bell,
  Share2,
  MessageCircle,
  Sparkles,
  AlertTriangle,
  ArrowLeft,
  ExternalLink
} from "lucide-react";
import { Button } from "antd";
import { Notification, useNotifications } from "@vojomo-core/shared";
import { timeAgo } from "./Notifications";

const notificationIcon: Record<
  Notification["type"],
  React.ComponentType<{ size?: number; className?: string }>
> = {
  share: Share2,
  comment: MessageCircle,
  ai_complete: Sparkles,
  spending_limit: AlertTriangle,
  broadcast: Bell,
  workflow_assignment: Bell,
  workflow_artifact_ready: Sparkles
};

const notificationTypeLabel: Record<Notification["type"], string> = {
  share: "Shared with you",
  comment: "Comment",
  ai_complete: "AI Complete",
  spending_limit: "Spending Limit",
  broadcast: "Announcement",
  workflow_assignment: "Workflow Assignment",
  workflow_artifact_ready: "Artifact Ready"
};

export function NotificationDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { markRead } = useNotifications();

  const notification: Notification | undefined = location.state?.notification;

  useEffect(() => {
    if (!notification) {
      navigate("/notifications", { replace: true });
      return;
    }
    if (!notification.is_read) {
      markRead(id!);
    }
  }, [id, notification, navigate, markRead]);

  if (!notification) return null;

  const Icon = notificationIcon[notification.type] ?? Bell;

  return (
    <div className="w-full mx-auto py-8 px-4">
      <Button
        type="text"
        icon={<ArrowLeft size={16} />}
        className="mb-6 -ml-2 text-gray-500"
        onClick={() => navigate("/notifications")}
      >
        Back to notifications
      </Button>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-full bg-purple-50 shrink-0">
            <Icon size={24} className="text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
              {notificationTypeLabel[notification.type]}
            </span>
            <h1 className="text-lg font-bold mt-1 leading-snug">
              {notification.title}
            </h1>
          </div>
        </div>

        <p className="mt-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {notification.message}
        </p>

        <p className="mt-3 text-xs text-gray-400">
          {timeAgo(notification.created_at)}
        </p>

        {notification.link && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <Button
              type="primary"
              icon={<ExternalLink size={14} />}
              onClick={() => navigate(notification.link!)}
            >
              View resource
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
